import { useState, useEffect, useCallback } from "react";
import { useApp } from "../context/AppContext";

const PRIORITY_MAP = {
  low: { label: "Low", color: "#6b7280", bg: "rgba(107,114,128,0.1)" },
  medium: { label: "Medium", color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  high: { label: "High", color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
  urgent: { label: "Urgent", color: "#dc2626", bg: "rgba(220,38,38,0.15)" },
};

export default function QABoardView() {
  const { apiFetch, flash, dark, t, card, btnP, btnG, btnSm, setActiveId, setView } = useApp();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(null);
  const [filterProject, setFilterProject] = useState("");
  const [rejectingTask, setRejectingTask] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [detailTask, setDetailTask] = useState(null); // відкрита задача для перевірки QA
  const [specUrlEdit, setSpecUrlEdit] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/api/kanban/qa-board");
      const d = await r.json();
      if (Array.isArray(d)) setTasks(d);
    } catch (e) { console.error("QA board load:", e); }
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const moveToDev = async (taskId) => {
    setMoving(taskId);
    try {
      const r = await apiFetch(`/api/kanban/tasks/${taskId}/move-to-dev`, { method: "POST" });
      const d = await r.json();
      if (r.ok) {
        flash(`Перенесено у «${d.newColumnTitle}»`);
        setTasks(prev => prev.filter(tk => tk.id !== taskId));
      } else {
        flash("Помилка: " + (d.error || r.status));
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setMoving(null);
  };

  const openReject = (task) => { setRejectingTask(task); setRejectReason(""); };
  const closeReject = () => { setRejectingTask(null); setRejectReason(""); setRejectSubmitting(false); };

  const submitReject = async () => {
    if (!rejectingTask) return;
    const reason = rejectReason.trim();
    if (!reason) { flash("Опиши причини, чому задача не пройшла"); return; }
    setRejectSubmitting(true);
    try {
      const r = await apiFetch(`/api/kanban/tasks/${rejectingTask.id}/qa-reject`, {
        method: "POST",
        body: JSON.stringify({ comment: reason }),
      });
      const d = await r.json();
      if (r.ok) {
        flash("Задача повернута розробнику");
        setTasks(prev => prev.filter(tk => tk.id !== rejectingTask.id));
        closeReject();
      } else {
        flash("Помилка: " + (d.error || r.status));
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setRejectSubmitting(false);
  };

  const openProject = (projId) => {
    if (!projId) return;
    setActiveId(projId);
    setView("kanban");
  };

  const projects = [...new Set(tasks.map(tk => tk.proj_id).filter(Boolean))];
  const visibleTasks = filterProject ? tasks.filter(tk => tk.proj_id === filterProject) : tasks;

  const projectsCount = projects.length;
  const tasksCount = tasks.length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>🧪 QA-доска · усі проекти</h2>
        <span style={{ fontSize: 11, color: t.textMuted }}>
          {tasksCount} задач{tasksCount === 1 ? "а" : tasksCount < 5 ? "і" : ""} з {projectsCount} проектів
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {projectsCount > 0 && (
            <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontFamily: "inherit" }}>
              <option value="">Усі проекти</option>
              {projects.map(pid => {
                const sample = tasks.find(tk => tk.proj_id === pid);
                return <option key={pid} value={pid}>{sample?.proj_name || pid}</option>;
              })}
            </select>
          )}
          <button onClick={load} style={{ ...btnG, fontSize: 11 }}>↻ Оновити</button>
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>}

      {!loading && tasksCount === 0 && (
        <div style={{ ...card, padding: 40, textAlign: "center", color: t.textMuted }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Черга QA пуста</div>
          <div style={{ fontSize: 12 }}>Задачі з'являться тут автоматично, коли розробник перетягне їх у колонку «На перевірці (QA)» в проектній канбан-дошці.</div>
        </div>
      )}

      {/* Reject modal */}
      {rejectingTask && (
        <div onClick={closeReject} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12,
            width: "100%", maxWidth: 520, padding: 22,
            boxShadow: dark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 12px 40px rgba(0,0,0,0.18)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 18, color: "#ef4444" }}>✗</span>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.text }}>Не пройшла тестування</h3>
            </div>
            <div style={{ fontSize: 11.5, color: t.textMuted, marginBottom: 14, marginLeft: 28 }}>
              <div style={{ marginBottom: 2 }}><strong>{rejectingTask.title}</strong></div>
              <div>проект: {rejectingTask.proj_name || rejectingTask.proj_id}</div>
            </div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: t.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Причини (видно SEO-шніку у задачі)
            </label>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
              placeholder="Опиши що не так, які кроки відтворити, посилання на скріни/відео..."
              rows={6}
              style={{
                width: "100%", boxSizing: "border-box",
                background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`,
                borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit",
                resize: "vertical", marginBottom: 14,
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={closeReject} disabled={rejectSubmitting}
                style={{ ...btnG, fontSize: 12 }}>Скасувати</button>
              <button onClick={submitReject} disabled={rejectSubmitting || !rejectReason.trim()}
                style={{
                  background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff", border: "none",
                  borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 700,
                  cursor: rejectReason.trim() ? "pointer" : "not-allowed",
                  opacity: rejectSubmitting || !rejectReason.trim() ? 0.6 : 1, fontFamily: "inherit",
                }}>
                {rejectSubmitting ? "Відправляю..." : "Відхилити та повернути"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Деталі задачі для перевірки QA ── */}
      {detailTask && (
        <div onClick={() => setDetailTask(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: 640, maxWidth: "100%", maxHeight: "88vh", overflow: "auto", padding: 22 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>{detailTask.title}</h3>
              <button onClick={() => setDetailTask(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: t.textMuted }}>✕</button>
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
              <span>Проект: <strong>{detailTask.proj_name || detailTask.proj_id}</strong></span>
              {(detailTask.author_full_name || detailTask.author_name) && <span>Автор: {detailTask.author_full_name || detailTask.author_name}</span>}
              {(detailTask.assignee_full_name || detailTask.assignee_name) && <span>Виконавець: {detailTask.assignee_full_name || detailTask.assignee_name}</span>}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Посилання на задачу</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  value={specUrlEdit}
                  onChange={e => setSpecUrlEdit(e.target.value)}
                  onBlur={async () => {
                    if (specUrlEdit === (detailTask.spec_url || "")) return;
                    await apiFetch(`/api/kanban/tasks/${detailTask.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ spec_url: specUrlEdit }) });
                    setDetailTask(prev => ({ ...prev, spec_url: specUrlEdit }));
                    setTasks(prev => prev.map(tk => tk.id === detailTask.id ? { ...tk, spec_url: specUrlEdit } : tk));
                  }}
                  placeholder="https://..."
                  style={{ flex: 1, padding: "7px 10px", borderRadius: 6, border: `1px solid ${t.border}`, fontSize: 13, color: t.text, background: t.bg, fontFamily: "inherit", outline: "none" }}
                />
                {specUrlEdit && (
                  <a href={specUrlEdit} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6366f1", fontWeight: 600, textDecoration: "none", padding: "7px 12px", borderRadius: 6, border: "1px solid #6366f1", whiteSpace: "nowrap" }}>
                    📎 Відкрити
                  </a>
                )}
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Опис</div>
            {detailTask.description
              ? <div style={{ fontSize: 14, color: t.text, lineHeight: 1.6, marginBottom: 20, overflowWrap: "break-word", wordBreak: "break-word" }}
                  dangerouslySetInnerHTML={{ __html: detailTask.description }} />
              : <div style={{ fontSize: 14, color: t.textMuted, fontStyle: "italic", marginBottom: 20 }}>Опис не заповнено</div>}
            <div style={{ display: "flex", gap: 10, borderTop: `1px solid ${t.border}`, paddingTop: 14 }}>
              <button onClick={() => { const tk = detailTask; setDetailTask(null); moveToDev(tk.id); }}
                style={{ background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                ✓ Пройшла — перенести в Dev
              </button>
              <button onClick={() => { const tk = detailTask; setDetailTask(null); openReject(tk); }}
                style={{ background: "transparent", color: "#ef4444", border: "1px solid #ef4444", borderRadius: 6, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                ✗ Не пройшла
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && visibleTasks.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visibleTasks.map(tk => {
            const pr = PRIORITY_MAP[tk.priority] || PRIORITY_MAP.medium;
            const assignee = tk.assignee_full_name || (tk.assignee_name ? tk.assignee_name.split("@")[0] : null);
            const author = tk.author_full_name || (tk.author_name ? tk.author_name.split("@")[0] : null);
            return (
              <div key={tk.id} style={{
                ...card,
                padding: "14px 16px",
                display: "flex",
                gap: 14,
                alignItems: "flex-start",
                borderLeft: `3px solid #f59e0b`,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span
                      onClick={() => openProject(tk.proj_id)}
                      title="Відкрити проект"
                      style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", padding: "2px 8px", borderRadius: 4, background: dark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.08)", cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.4 }}
                    >
                      {tk.proj_name || tk.proj_id || "—"}
                    </span>
                    {tk.external_dev && <span title="Сторонні розробники" style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 4, background: "rgba(249,115,22,0.14)", color: "#f97316" }}>🌐 Сторонні</span>}
                    <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: pr.bg, color: pr.color, textTransform: "uppercase" }}>{pr.label}</span>
                    <span style={{ fontSize: 10, color: t.textMuted }}>колонка: {tk.column_title}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: t.text, marginBottom: 4 }}>{tk.title}</div>
                  {tk.description && <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6, maxHeight: 60, overflow: "hidden" }}>{tk.description.slice(0, 200)}{tk.description.length > 200 ? "…" : ""}</div>}
                  <div style={{ fontSize: 10, color: t.textMuted, display: "flex", gap: 12 }}>
                    {author && <span>Автор: {author}</span>}
                    {assignee && <span>Виконавець: {assignee}</span>}
                    {tk.updated_at && <span>Оновлено: {new Date(tk.updated_at).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0, minWidth: 170 }}>
                  <button
                    onClick={() => moveToDev(tk.id)}
                    disabled={moving === tk.id}
                    style={{
                      background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", border: "none",
                      borderRadius: 6, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                      opacity: moving === tk.id ? 0.6 : 1, fontFamily: "inherit", whiteSpace: "nowrap",
                    }}
                  >
                    {moving === tk.id ? "..." : "✓ Перенести в Dev"}
                  </button>
                  <button
                    onClick={() => openReject(tk)}
                    style={{
                      background: "transparent", color: "#ef4444", border: "1px solid #ef4444",
                      borderRadius: 6, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                      fontFamily: "inherit", whiteSpace: "nowrap",
                    }}
                  >
                    ✗ Не пройшла
                  </button>
                  <button
                    onClick={() => { setDetailTask(tk); setSpecUrlEdit(tk.spec_url || ""); }}
                    style={{ background: "transparent", color: "#6366f1", border: "1px solid #6366f1", borderRadius: 6, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                  >
                    🔍 Відкрити / перевірити
                  </button>
                  <button
                    onClick={() => openProject(tk.proj_id)}
                    style={{ ...btnSm, fontSize: 10, padding: "4px 10px" }}
                  >
                    Відкрити проект
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
